"use client";

import React from "react";

import { cn } from "@/lib/utils";
import { useMotionTemplate, useTransform } from "framer-motion";

import Chip from "@/components/bastian-keller/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import MorphingNumber from "@/components/bastian-keller/MorphingNumber";
import PhotoCard from "@/components/bastian-keller/PhotoCard";
import { MotionDiv, MotionP } from "@/components/global/utils/motionTags";
import { promoteLayer } from "@/components/global/utils/motionLayer";
import { useMotionMode } from "@/components/global/utils/motionMode";
import {
  CROSSING_OFFSET,
  REVEAL_OFFSET,
  useReveal,
  useRevealProgress,
} from "@/components/global/utils/scrollReveal";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

// the entry that owns the year readout is the last one whose top edge has crossed
// this fraction of the viewport, which keeps the switch in step with the sticky year
const FOCUS_RATIO = 0.45;

/**
 * Year rail geometry, in rem. The Figma stroke is 2px dashes on a 17px pitch at
 * 1920 (an 8px rem), with a 3px tick over the active year. Everything else — where
 * the years sit, and therefore where the tick and the dashes must land — is read
 * from the rendered list rather than kept in a parallel table of numbers, so the
 * two can never drift apart when a size, a leading or a breakpoint changes.
 */
const RAIL_DASH = 0.25;
const RAIL_PITCH = 2.125;
const RAIL_TICK_HEIGHT = 0.375;

type RailGeometry = {
  /** px, relative to the sticky frame: the vertical centre of each year */
  centers: number[];
  /** px: centre of every dash, one guaranteed on each year */
  dashes: number[];
  /** px: top edge of the first dash */
  top: number;
  dash: number;
  tick: number;
};

/**
 * The dashes are spaced so that a whole number of them fit between one year and
 * the next — as close to the Figma pitch as that allows — and the pattern is
 * phased off the first year's centre. That is what makes the tick sit on a dash
 * for every year, not only the first: a fixed 17px pitch against a 58px year
 * pitch never lines up again after the top.
 */
function measureRail(frame: HTMLElement, list: HTMLElement, years: (HTMLElement | null)[]): RailGeometry | null {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
  if (!rem) return null;

  // offsetTop chains: the items are positioned inside the absolutely-positioned
  // list, which is positioned inside the sticky frame
  const centers = years
    .filter((year): year is HTMLElement => Boolean(year))
    .map((year) => list.offsetTop + year.offsetTop + year.offsetHeight / 2);
  if (centers.length === 0 || frame.offsetHeight === 0) return null;

  const dash = RAIL_DASH * rem;
  const yearPitch = centers.length > 1 ? centers[1] - centers[0] : RAIL_PITCH * rem;
  const perYear = Math.max(1, Math.round(yearPitch / (RAIL_PITCH * rem)));
  const pitch = yearPitch / perYear;

  // one dash above the first year and one below the last, as the Figma guide runs
  const first = centers[0] - pitch;
  const count = perYear * (centers.length - 1) + 3;
  const dashes = Array.from({ length: count }, (_, i) => first + i * pitch);

  return { centers, dashes, top: first - dash / 2, dash, tick: RAIL_TICK_HEIGHT * rem };
}

/** Each dash is its own element, snapped to the pixel — a gradient with a fractional pitch smears. */
function RailDashes({ geometry, className }: { geometry: RailGeometry; className: string }) {
  return (
    <>
      {geometry.dashes.map((center, i) => (
        <div
          key={i}
          className={cn("absolute left-[0.03rem] w-[1.5rem]", className)}
          style={{ top: Math.round(center - geometry.dash / 2) - Math.round(geometry.top), height: Math.max(1, Math.round(geometry.dash)) }}
        />
      ))}
    </>
  );
}

/** One spring for the tick and the fill, so they travel the rail as one piece. */
const RAIL_SPRING = {
  type: "spring",
  stiffness: 120,
  damping: 22,
  mass: 0.8,
} as const;

function YearRail({
  items,
  activeIndex,
  reduceMotion,
}: {
  items: PageBastianKellerSchema["timelineItems"];
  activeIndex: number;
  reduceMotion: boolean;
}) {
  const frameRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const yearRefs = React.useRef<(HTMLLIElement | null)[]>([]);
  const [geometry, setGeometry] = React.useState<RailGeometry | null>(null);

  // measured before first paint, then again whenever the frame changes size —
  // which is how a viewport resize, and with it the rem, reaches the rail
  React.useLayoutEffect(() => {
    const frame = frameRef.current;
    const list = listRef.current;
    if (!frame || !list) return;

    const measure = () => setGeometry(measureRail(frame, list, yearRefs.current.slice(0, items.length)));
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(list);
    return () => observer.disconnect();
  }, [items.length]);

  const transition = reduceMotion ? { duration: 0 } : RAIL_SPRING;
  const active = geometry?.centers[activeIndex] ?? geometry?.centers[0] ?? 0;
  const railHeight = geometry ? geometry.dashes[geometry.dashes.length - 1] + geometry.dash / 2 - geometry.top : 0;

  return (
    <div
      ref={frameRef}
      /*
        Below lg the bar is fixed over the top of the page, so the rail parks
        under it rather than at the very top of the viewport.
      */
      className="sticky top-[7.25rem] h-[37rem] w-[9.5rem] lg:top-0 lg:h-[75.625rem] lg:w-[12.25rem]">
      {geometry && (
        <>
          {/* dashed guide, at a tenth strength */}
          <div
            className="absolute inset-x-0"
            style={{ top: Math.round(geometry.top), height: Math.round(railHeight) }}>
            <RailDashes
              geometry={geometry}
              className="bg-black/10"
            />
          </div>

          {/*
            Progress fill — the same dashes at full strength, revealed down to the
            active year. Both layers are built from the same positions, so a solid
            dash always sits exactly over the faint one it replaces.
          */}
          <MotionDiv
            className="absolute inset-x-0 overflow-hidden"
            style={{ top: Math.round(geometry.top) }}
            initial={false}
            animate={{ height: active + geometry.dash / 2 - geometry.top }}
            transition={transition}>
            <RailDashes
              geometry={geometry}
              className="bg-[#112628]"
            />
          </MotionDiv>

          {/*
            A single tick that travels the rail, rather than one mounted and
            unmounted per year — that is what lets it glide between them.
          */}
          <MotionDiv
            className="absolute left-0 h-[0.375rem] w-[2.125rem] bg-[#112628] lg:w-[3.1875rem]"
            initial={false}
            animate={{ top: active - geometry.tick / 2 }}
            transition={transition}
          />
        </>
      )}

      <ul
        ref={listRef}
        className="absolute left-[3.25rem] top-[3.125rem] m-0 flex list-none flex-col gap-[4rem] p-0 lg:left-[5.75rem] lg:top-[33rem]">
        {items.map((item, index) => (
          <li
            key={index}
            ref={(node) => {
              yearRefs.current[index] = node;
            }}
            className="relative">
            <span
              className={cn(
                "block font-geist text-[1.75rem] leading-none transition-all duration-500 ease-out lg:text-[2.5rem] lg:leading-[1.3]",
                index === activeIndex ? "text-black translate-x-[0.75rem]" : "text-black/20 translate-x-0",
              )}>
              {item?.year}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Half-travel of each column as its entry crosses the viewport. Deliberately
 * small — the point is that the light text column pulls slightly ahead of the
 * heavy photo, not that either of them visibly moves on its own.
 */
const PHOTO_DRIFT = 2;
const TEXT_DRIFT = 5;

/** Photo, chip and paragraph — three slices of the entry's own scroll range. */
const ENTRY_PARTS = 3;

function TimelineEntry({
  item,
  entryRef,
  reduceMotion,
  parallax,
}: {
  item: PageBastianKellerSchema["timelineItems"][number];
  entryRef: (node: HTMLDivElement | null) => void;
  reduceMotion: boolean;
  parallax: boolean;
}) {
  const rowRef = React.useRef<HTMLDivElement>(null);

  /*
    Two ranges off the same element. The drift runs the entry's whole crossing of
    the viewport, so the two columns keep separating the entire time it is on
    screen; the reveal only needs the window in which the entry arrives.
  */
  const drift = useRevealProgress(rowRef, CROSSING_OFFSET, !parallax);

  const photoDrift = parallax ? PHOTO_DRIFT : 0;
  const textDrift = parallax ? TEXT_DRIFT : 0;
  const photoY = useTransform(drift, [0, 1], [`${photoDrift}rem`, `${-photoDrift}rem`]);
  const textY = useTransform(drift, [0, 1], [`${textDrift}rem`, `${-textDrift}rem`]);

  const progress = useRevealProgress(rowRef, REVEAL_OFFSET, reduceMotion);
  const photo = useReveal(progress, 0, ENTRY_PARTS, reduceMotion);
  const chip = useReveal(progress, 1, ENTRY_PARTS, reduceMotion);
  const text = useReveal(progress, 2, ENTRY_PARTS, reduceMotion);

  // the mask opens upward while the card rides up behind it, so the card arrives
  // from under its own edge rather than simply fading in
  const photoClipTop = useTransform(photo.reveal, [0, 1], [100, 0]);
  const photoClip = useMotionTemplate`inset(${photoClipTop}% 0% 0% 0%)`;

  return (
    <div
      ref={(node) => {
        rowRef.current = node;
        entryRef(node);
      }}
      className="flex flex-col lg:flex-row lg:items-start gap-[2.5rem] lg:gap-[3rem]">
      {/*
        The drift rides on the wipe wrapper, which only drives clip-path, so it
        never fights the `y` the card itself uses to rise into view.
      */}
      <MotionDiv
        style={{ y: photoY, clipPath: photoClip }}
        /*
          Promoted: this wrapper drifts for the entry's whole crossing and carries
          the largest photo on the page. It changes no stacking behaviour — the
          clip-path above already makes it a stacking context, and permanently,
          so the sticky year below still reads the same backdrop it did before.
          The paragraph column opposite is left alone: type only, and it is the
          one thing on this section a composited layer would visibly cost.
        */
        transformTemplate={parallax ? promoteLayer : undefined}
        className="w-full lg:w-[97.375rem]">
        <MotionDiv style={{ y: photo.y }}>
          <PhotoCard
            /* the mobile frame is drawn much tighter than the desktop one */
            className="w-full gap-[0.28rem] lg:gap-[0.65rem]"
            frameClassName="p-[0.26rem] lg:p-[0.9rem]"
            captionClassName="text-[1.5rem] px-[0.79rem] py-[0.53rem] lg:text-[2rem] lg:px-[0.75rem] lg:py-[0.5rem]"
            aspect="aspect-[763/741]"
            image={item?.image}
            caption={item?.imageCaption}
          />
        </MotionDiv>
      </MotionDiv>

      <MotionDiv
        style={{ y: textY }}
        className="flex flex-col items-start gap-[1.75rem] lg:w-[46.125rem] lg:gap-[2.125rem]">
        {item?.chip?.text && (
          <MotionDiv style={{ opacity: chip.reveal, y: chip.y }}>
            <Chip
              image={item?.chip?.icon}
              text={item?.chip?.text}
            />
          </MotionDiv>
        )}

        {item?.text && (
          <MotionP
            style={{ opacity: text.reveal, y: text.y }}
            className="m-0 text-black font-geist text-[2.25rem] leading-[1.3] lg:text-[2.75rem]">
            {item.text}
          </MotionP>
        )}
      </MotionDiv>
    </div>
  );
}

export default function TimelineSection({ data, className }: { data: PageBastianKellerSchema; className?: string }) {
  const items = data?.timelineItems;
  const entryRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);

  // below lg the photo stacks on top of the text rather than beside it, where two
  // different drift rates would just breathe the gap between them open and shut
  const { reduceMotion, parallax } = useMotionMode();

  React.useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      const focus = window.innerHeight * FOCUS_RATIO;
      let next = 0;
      entryRefs.current.forEach((entry, index) => {
        if (entry && entry.getBoundingClientRect().top <= focus) next = index;
      });
      setActiveIndex(next);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [items?.length]);

  if (!items || items.length === 0) return null;

  return (
    <section className={cn("relative bg-[#F3F2F1] overflow-clip", className)}>
      <div className="relative mx-auto w-[min(240rem,100%)]">
        <DecorationLine
          className="left-[1rem] lg:left-[7.125rem]"
          orientation="vertical"
          style="dark"
        />
        <DecorationLine
          className="right-[1rem] lg:right-[7rem]"
          orientation="vertical"
          style="dark"
        />

        {/*
          Entries — offset to the right of the year rail, as in the Figma frame.
          Nothing from here down carries a z-index on purpose: the sticky year below
          relies on mix-blend-difference, which only blends against what is painted
          beneath it inside the same stacking context. Giving these wrappers a
          z-index would isolate that year onto its own layer with an empty backdrop
          and it would render flat white. Paint order comes from DOM order instead.
        */}
        <div className="flex relative flex-col gap-[5rem] ml-[11.625rem] mr-[2rem] py-[16rem] lg:gap-[50rem] lg:ml-[71.375rem] lg:mr-0 lg:w-[145.125rem] lg:pb-[30rem] lg:pt-[30rem]">
          {items.map((item, index) => (
            <TimelineEntry
              key={index}
              item={item}
              reduceMotion={reduceMotion}
              parallax={parallax}
              entryRef={(node) => {
                entryRefs.current[index] = node;
              }}
            />
          ))}
        </div>

        {/*
          Sticky year readout. White text under mix-blend-difference reads as near
          black over the section background and inverts wherever it crosses a photo,
          which is how the Figma layer is built.
        */}
        {/* the giant readout is a desktop-only layer; mobile keeps only the rail */}
        <div className="hidden absolute inset-x-0 bottom-0 pointer-events-none top-[-15rem] lg:block">
          {/*
            The blend sits on the sticky element, not on the text inside it:
            `position: sticky` is itself a stacking context, so a nested blended
            child would only ever see an empty backdrop and paint flat white.
          */}
          <div className="flex sticky top-0 justify-center px-[1.25rem] pb-[8rem] pt-[20rem] mix-blend-difference lg:pt-[37.5rem]">
            <p className="m-0 leading-none text-white uppercase font-funnel text-[10rem] font-[800] tracking-[0.02em] lg:text-[18.75rem]">
              <MorphingNumber
                value={items[activeIndex]?.year}
                reduceMotion={reduceMotion}
              />
            </p>
          </div>
        </div>

        {/* sticky year rail */}
        <div className="absolute inset-y-0 pointer-events-none left-[2.125rem] lg:left-[12.125rem]">
          <YearRail
            items={items}
            activeIndex={activeIndex}
            reduceMotion={reduceMotion}
          />
        </div>
      </div>

      <DecorationLine
        className="bottom-0"
        orientation="horizontal"
        style="dark"
      />
    </section>
  );
}
